import * as adminApi from 'api/adminApi';
import { getLocalization } from 'global/global';
import { AdminGroupDraft } from 'Interfaces/Admin';
import { setSuperUserGroupFlags } from 'reducers/admin/account';
import { deactivateAdminGroups, upsertAdminGroup } from 'reducers/admin/groups';
import { pendingToast } from './adminToast';
import { reloadAdminUsers } from './adminUserActions';

/** Replaces WebContent-src/admin/sync/GroupSync.js. */

const errorMessage = (response: adminApi.AdminResponse): string =>
  response.errorCode ? getLocalization(`error${response.errorCode}`) : getLocalization('errorSavingGroup');

export const saveAdminGroup = (group: AdminGroupDraft, wasActive?: boolean) => {
  return async (dispatch): Promise<adminApi.AdminGroupSaveResult> => {
    const notify = pendingToast(getLocalization('savingGroup'));
    try {
      const response = await adminApi.saveGroup(group);
      if (response.status !== 'OK' || !response.group) {
        notify.fail(errorMessage(response));
        return { saved: false };
      }
      const saved = response.group;
      notify.succeed(getLocalization('groupSaved'));

      dispatch(upsertAdminGroup(saved));
      dispatch(setSuperUserGroupFlags(saved.superuser_group === true));

      /**
       *  Forms picked on a group with no form list of its own only stick once
       *  they are turned into a form list. Without this the group saves and
       *  silently keeps no forms.
       */
      if (!saved.surveyId && saved.forms && saved.forms.length > 0) {
        const identifier = saved.surveyIdentifier || 'true';
        const name = saved.formListName || `${saved.name} Form list`;
        try {
          await adminApi.addFormsToFormList(saved.forms, identifier, name, saved.id);
          const refreshed = await adminApi.fetchGroup(saved.id);
          if (refreshed) {
            dispatch(upsertAdminGroup(refreshed));
          }
        } catch (e) {
          console.log(e);
        }
      }

      /**
       *  Deactivating a group deactivates its users server side, so the user
       *  list has to be refetched — but only then.
       */
      if (wasActive !== undefined && wasActive !== saved.active) {
        void dispatch(reloadAdminUsers());
      }

      return { saved: true, group: saved };
    } catch (e) {
      console.log(e);
      notify.fail(getLocalization('errorSavingGroup'));
      return { saved: false };
    }
  };
};

export const deleteAdminGroups = (ids: number[], mode: adminApi.GroupDeleteMode) => {
  return async (dispatch): Promise<boolean> => {
    const notify = pendingToast(`${getLocalization('deleting')} ${ids.length}`);
    try {
      const response = await adminApi.deleteGroups(ids, mode);
      if (response.status === 'FAIL') {
        notify.fail(getLocalization('deletefail'));
        return false;
      }
      dispatch(deactivateAdminGroups(response.ids || []));
      if (response.status === 'PARTIAL') {
        notify.fail(getLocalization('deletePartial'));
      } else {
        notify.succeed(getLocalization('deleteSuccess'));
      }
      return true;
    } catch (e) {
      console.log(e);
      notify.fail(getLocalization('deletefail'));
      return false;
    }
  };
};
